import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
export const formatSpread = (spread) => (spread > 0 ? `+${spread}` : `${spread}`);
// The side we want: the edge pick when the signals agree, otherwise the CFB
// model v2 core pick (the edge pick nets out to "no side" on some core games).
export const getEdgePlay = (context) => {
    if (!context)
        return null;
    const pick = context.edge_pick;
    const v2 = context.model_v2?.pick;
    // Quote the play at the current bettable line (the pipeline writes the same number
    // into betting_analysis), not the line the v2 model run saw
    const current = context.line_movement?.current_spread;
    const lineFor = (side, fallback) => current === null || current === undefined ? fallback : side === 'home' ? current : current === 0 ? 0 : -current;
    if (pick?.side && pick.team) {
        return {
            side: pick.side,
            team: pick.team,
            spread: lineFor(pick.side, pick.spread),
            drivers: pick.drivers,
            core: !!v2?.core && v2.side === pick.side
        };
    }
    if (v2?.core) {
        return {
            side: v2.side,
            team: v2.team,
            spread: lineFor(v2.side, v2.line),
            drivers: [`model v2 edge ${v2.edge.toFixed(1)} pts`],
            core: true
        };
    }
    return null;
};
// Team names come full ("Syracuse Orange") or short ("Syracuse")
export const isSameTeam = (a, b) => !!a && !!b && (a.startsWith(b) || b.startsWith(a));
// Only surface model confidence / bet grade when they are good news
export const isStrongConfidence = (level) => level === 'HIGH' || level === 'MEDIUM_PLUS' || level === 'MEDIUM-PLUS' || level === 'ELITE';
export const isStrongGrade = (grade) => !!grade && /^[AB]/i.test(grade);
export const getEdgeTier = (score, hasPlay) => {
    if (score >= 60)
        return { label: 'Prime Edge', color: '#22c55e', bars: 5 };
    if (score >= 40)
        return { label: 'Strong Edge', color: '#22c55e', bars: 4 };
    if (score >= 20)
        return { label: 'Solid Edge', color: '#3b82f6', bars: 3 };
    if (score > 0 || hasPlay)
        return { label: 'Lean', color: '#3b82f6', bars: 2 };
    return { label: 'On Watch', color: '#9ca3af', bars: 1 };
};
const prettyDriver = (driver) => {
    const text = driver.replace(/^model v2 edge/i, 'Model edge');
    return text.charAt(0).toUpperCase() + text.slice(1);
};
const Chip = ({ color = '#9ca3af', strong, children }) => (_jsx("span", { style: {
        display: 'inline-block',
        fontSize: '11px',
        fontWeight: strong ? 700 : 500,
        color: strong ? '#fff' : 'var(--bs-body-color)',
        backgroundColor: strong ? color : 'transparent',
        border: `1px solid ${color}`,
        borderRadius: '999px',
        padding: '2px 8px',
        margin: '0 4px 4px 0'
    }, children: children }));
// Opener -> current line, told from the play team's side when there is a play
const LineMovement = ({ context, play, homeTeam, awayTeam }) => {
    const lm = context.line_movement;
    const open = lm?.opening_spread ?? lm?.first_spread;
    const now = lm?.current_spread;
    if (open === null || open === undefined || now === null || now === undefined)
        return null;
    const side = play?.side ?? 'home';
    const team = play ? play.team : homeTeam;
    const flip = side === 'home' ? 1 : -1;
    const sideOpen = open * flip;
    const sideNow = now * flip;
    const delta = Math.round((sideNow - sideOpen) * 100) / 100;
    let caption;
    let color = '#9ca3af';
    if (Math.abs(delta) < 0.25) {
        caption = 'Holding at the opener';
    }
    else if (!play) {
        caption = `Moved ${Math.abs(delta)} pts toward ${delta < 0 ? homeTeam : awayTeam}`;
    }
    else if (delta < 0) {
        // Play team is laying more / getting fewer points: the market is moving our way
        caption = `Market moving our way: ${Math.abs(delta)} pts toward ${team}`;
        color = '#22c55e';
    }
    else {
        caption = `Better number: ${delta} pts more than the opener`;
        color = '#22c55e';
    }
    return (_jsxs("div", { className: "mb-2", style: { fontSize: '12px' }, children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("span", { style: { color: '#9ca3af' }, children: [_jsx("i", { className: "fas fa-chart-line me-1" }), "Line \u00B7 ", team.split(' ').pop()] }), _jsxs("span", { style: { fontWeight: 600, color: 'var(--bs-body-color)' }, children: [_jsxs("span", { style: { color: '#9ca3af', fontWeight: 500 }, children: ["Open ", formatSpread(sideOpen)] }), _jsx("i", { className: "fas fa-arrow-right mx-2", style: { fontSize: '10px', color } }), "Now ", formatSpread(sideNow)] })] }), _jsx("div", { style: { fontSize: '11px', color, textAlign: 'right', marginTop: '2px' }, children: caption })] }));
};
// Edge rank, strength meter, line movement and the reasons behind the play.
// Renders nothing for games without edge data.
const EdgeSection = ({ rank, context, homeTeam, awayTeam }) => {
    if (!context || rank === undefined)
        return null;
    const play = getEdgePlay(context);
    const tier = getEdgeTier(context.edge_score, !!play);
    // Line movement has its own row, so leave it out of the chips
    const drivers = (play?.drivers ?? []).filter((d) => !/^line moved/i.test(d));
    const teaser = (context.teaser_legs ?? []).find((leg) => leg.model_agrees);
    const windUnder = context.wind_under;
    return (_jsxs("div", { className: "edge-section mb-3", style: { borderTop: '1px solid var(--bs-border-color)', paddingTop: '12px' }, children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-2", children: [_jsxs("span", { style: { fontSize: '12px', fontWeight: 700, color: tier.color }, children: [_jsx("i", { className: "fas fa-bolt me-1" }), tier.label] }), _jsx("span", { className: "d-flex align-items-center", title: `Edge rank #${rank}`, children: [1, 2, 3, 4, 5].map((i) => (_jsx("span", { style: {
                                width: '14px',
                                height: '6px',
                                borderRadius: '3px',
                                marginLeft: '3px',
                                backgroundColor: i <= tier.bars ? tier.color : 'var(--bs-border-color)'
                            } }, i))) })] }), _jsx(LineMovement, { context: context, play: play, homeTeam: homeTeam, awayTeam: awayTeam }), (play?.core || drivers.length > 0 || teaser || windUnder) ? (_jsxs("div", { className: "mt-2", children: [play?.core && _jsx(Chip, { color: "#22c55e", strong: true, children: "Core Play" }), drivers.map((d) => (_jsx(Chip, { children: prettyDriver(d) }, d))), teaser && (_jsxs(Chip, { color: "#f59e0b", strong: teaser.best, children: ["Teaser leg: ", teaser.team.split(' ').pop(), " ", formatSpread(teaser.line), " \u2192 ", formatSpread(teaser.teased_6)] })), windUnder && (_jsxs(Chip, { color: "#3b82f6", strong: true, children: [_jsx("i", { className: "fas fa-wind me-1" }), "Wind Under \u00B7 ", windUnder.forecast_wind_mph, " mph"] }))] })) : (!play && (_jsx("div", { style: { fontSize: '11px', color: '#9ca3af' }, children: "No play yet. Signals refresh daily." })))] }));
};
// Card headline: the edge play (with a Top Play badge for the top 3), or the
// model's lean when the edge signals don't pick a side.
export const EdgePlayHeader = ({ rank, play, playOdds, leanText, leanOdds }) => {
    const topPlay = play && rank !== undefined && rank <= 3;
    if (!play) {
        return (_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-3", style: { fontSize: '14px' }, children: [_jsxs("span", { style: { color: '#9ca3af' }, children: ["Model lean: ", _jsx("span", { style: { color: 'var(--bs-body-color)', fontWeight: 600 }, children: leanText })] }), leanOdds && _jsx("span", { style: { color: '#9ca3af', fontWeight: 600 }, children: leanOdds })] }));
    }
    return (_jsxs("div", { className: "mb-3", style: {
            border: `1px solid ${topPlay ? '#22c55e' : 'var(--bs-border-color)'}`,
            backgroundColor: topPlay ? 'rgba(34, 197, 94, 0.08)' : 'transparent',
            borderRadius: '8px',
            padding: '10px 12px'
        }, children: [_jsx("div", { className: "d-flex justify-content-between align-items-center mb-1", children: _jsx("span", { style: { fontSize: '11px', fontWeight: 700, letterSpacing: '0.04em', color: topPlay ? '#22c55e' : '#9ca3af' }, children: topPlay ? _jsxs(_Fragment, { children: [_jsx("i", { className: "fas fa-fire me-1" }), "TOP PLAY #", rank] }) : `EDGE PLAY${rank !== undefined ? ` #${rank}` : ''}` }) }), _jsxs("div", { className: "d-flex justify-content-between align-items-center", children: [_jsxs("span", { style: { fontSize: '18px', fontWeight: 700, color: 'var(--bs-body-color)' }, children: [play.team, play.spread !== null ? ` ${formatSpread(play.spread)}` : ''] }), playOdds && _jsx("span", { style: { fontSize: '18px', fontWeight: 700, color: '#22c55e' }, children: playOdds })] })] }));
};
export default EdgeSection;
